import type { ThemeGapValue } from '@mntn-dev/ui-theme'
import { cn } from '@mntn-dev/ui-utilities'
import { type MouseEvent, useEffect, useMemo, useRef, useState } from 'react'
import { Chip } from '../chip/chip.tsx'
import { Chips } from '../chip/chips.tsx'
import { MultiselectPlusMore } from './multiselect-plus-more.tsx'
import type {
  MultiselectChipsContainerProps,
  MultiselectOptionItem,
} from './types.ts'
import { useMultiselectContext } from './use-multiselect.ts'
import { getShowMoreAttributes, getShowMoreTooltip } from './utils.ts'

const columnGapValue: ThemeGapValue = '2'

export const MultiselectChipsContent = ({
  chipVariant,
  singleLine,
  disabled,
  listLocation,
  readOnly,
}: MultiselectChipsContainerProps) => {
  const { onRemove, selectedItems } = useMultiselectContext()

  const [showMoreNumber, setShowMoreNumber] = useState<number>()
  const [showMoreOffset, setShowMoreOffset] = useState<number>()
  const [showMoreTooltip, setShowMoreTooltip] = useState<string>()

  const chipContainerRef = useRef<HTMLDivElement | null>(null)

  const handleRemove =
    (item: MultiselectOptionItem) => (e: MouseEvent<HTMLButtonElement>) => {
      e.stopPropagation()
      e.preventDefault()

      onRemove?.(item)
    }

  const keepOnSameLine = useMemo(
    () => singleLine && listLocation === 'inside',
    [singleLine, listLocation]
  )

  useEffect(() => {
    if (chipContainerRef.current && keepOnSameLine) {
      const [showMoreNumber, showMoreOffset] = getShowMoreAttributes(
        chipContainerRef.current,
        columnGapValue
      )

      const showMoreTooltip = getShowMoreTooltip(selectedItems, showMoreNumber)

      setShowMoreNumber(showMoreNumber)
      setShowMoreTooltip(showMoreTooltip)
      setShowMoreOffset(showMoreOffset)
    }
  }, [selectedItems, keepOnSameLine])

  const variant =
    chipVariant ?? (listLocation === 'above' ? 'rounded-square' : 'pill')

  return (
    <>
      {selectedItems && selectedItems.length > 0 && (
        <Chips
          ref={chipContainerRef}
          gap={columnGapValue}
          className={cn({
            'opacity-0': showMoreNumber !== undefined,
            'flex-wrap': !keepOnSameLine,
            'flex-nowrap': keepOnSameLine,
          })}
        >
          {selectedItems.map((selectedItem) => (
            <Chip
              key={selectedItem.id}
              id={selectedItem.id}
              variant={variant}
              dismissible={!(disabled || readOnly)}
              onDismissClick={handleRemove(selectedItem)}
            >
              {selectedItem.name}
            </Chip>
          ))}
        </Chips>
      )}

      {showMoreNumber !== undefined && (
        <MultiselectPlusMore
          count={showMoreNumber}
          offset={showMoreOffset}
          tooltip={showMoreTooltip}
        />
      )}
    </>
  )
}
